import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import EstimateForm from '../../components/feature/EstimateForm';
import FinalCTA from '../home/components/FinalCTA';

const values = [
  { icon: 'ri-medal-2-line', title: 'Quality First', desc: 'Every project — regardless of size or budget — receives the same standard of exacting craftsmanship and care.', num: '500+', numLabel: 'Projects' },
  { icon: 'ri-heart-line', title: 'Customer Satisfaction', desc: 'We measure success not by the job completing, but by your reaction to walking into a room with your new floors.', num: '5.0★', numLabel: 'Rating' },
  { icon: 'ri-price-tag-line', title: 'Honest Pricing', desc: 'Detailed written quotes from day one. No hidden fees, no change-orders, no surprises — ever.', num: '0', numLabel: 'Hidden Fees' },
  { icon: 'ri-focus-3-line', title: 'Attention to Detail', desc: 'From expansion gaps to finish coat edges — precision in every step is what separates a good floor from a great one.', num: '15+', numLabel: 'Years' },
];

const trustItems = [
  { icon: 'ri-shield-check-line', title: 'Licensed & Insured', sub: 'State of Maryland certified contractor' },
  { icon: 'ri-map-pin-2-line', title: 'Maryland-Based', sub: 'Locally owned & operated since 2009' },
  { icon: 'ri-history-line', title: 'Proven Track Record', sub: '500+ residential projects completed' },
  { icon: 'ri-award-line', title: 'Master Craftsmen', sub: '15+ years hands-on hardwood expertise' },
];

const experience = [
  { icon: 'ri-home-3-line', title: 'Residential Focus', desc: 'Every project is someone\'s home — we treat it that way.' },
  { icon: 'ri-tools-line', title: 'Pro-Grade Equipment', desc: 'Dust containment, precision tools, premium materials.' },
  { icon: 'ri-leaf-line', title: 'Eco-Conscious', desc: 'Sustainable sourcing and responsible disposal practices.' },
  { icon: 'ri-time-line', title: 'On Schedule, Always', desc: 'We respect your time and deliver on every commitment.' },
];

export default function AboutPage() {
  const [formOpen, setFormOpen] = useState(false);

  return (
    <div className="min-h-screen font-sans">
      <Header onEstimateClick={() => setFormOpen(true)} />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden min-h-[500px] flex items-end">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/e6eddaab7ed7d5d1792a3220449ac9ea.jpg"
              alt="About Premier Hardwood Floors Maryland"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/70" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-transparent" />
          </div>
          <div className="relative max-w-7xl mx-auto px-6 w-full pt-36 pb-20">
            <nav className="flex gap-2 text-sm text-white/50 font-sans mb-6">
              <Link to="/" className="hover:text-gold transition-colors">Home</Link>
              <span>/</span>
              <span className="text-gold">About Us</span>
            </nav>
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 bg-gold/20 border border-gold/30 rounded-full px-4 py-1.5 mb-6">
                <i className="ri-map-pin-2-line text-gold text-sm" />
                <span className="font-sans text-gold text-xs font-semibold">Maryland-Based Since 2009</span>
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-white font-bold leading-tight mb-5">
                About Premier Hardwood Floors
              </h1>
              <p className="font-sans text-lg text-white/70 leading-relaxed">
                Built on craftsmanship, precision, and an unwavering commitment to Maryland homeowners — since day one.
              </p>
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="bg-white py-28 px-6">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-5 gap-16 items-center">
            <div className="lg:col-span-3">
              <span className="font-sans text-xs uppercase tracking-widest text-oak font-semibold">Our Story</span>
              <h2 className="font-serif text-4xl text-walnut font-bold mt-3 mb-7 leading-tight">
                A Business Built on Craftsmanship
              </h2>
              <div className="space-y-6 font-sans text-base text-charcoal/65 leading-relaxed">
                <p>
                  Premier Hardwood Floors was founded on one conviction: every Maryland homeowner deserves hardwood floors installed with the same precision and care a master craftsman would apply to their own home.
                </p>
                <p>
                  Over 15 years, we&apos;ve built a reputation on showing up on time, communicating with total honesty, and consistently delivering results that exceed expectations. Our work spans historic colonial homes in Annapolis, modern townhomes in Silver Spring, and custom builds throughout Montgomery and Howard Counties.
                </p>
                <p>
                  We&apos;re a locally owned and operated company, deeply invested in the communities we serve. Every project — whether a single room or a full home — receives our complete attention and the highest standard of workmanship.
                </p>
              </div>
              <blockquote className="border-l-4 border-gold pl-6 mt-8">
                <p className="font-serif text-xl text-walnut leading-relaxed italic">
                  &ldquo;We don&apos;t just install floors. We transform spaces and protect generational investments.&rdquo;
                </p>
                <div className="mt-3 font-sans text-sm text-charcoal/50">— Premier Hardwood Floors Founder</div>
              </blockquote>
            </div>
            <div className="lg:col-span-2">
              <div className="rounded-2xl overflow-hidden relative">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20hardwood%20flooring%20craftsman%20expert%20at%20work%20Maryland%20residential%20home%20beautiful%20finished%20oak%20floor%20installation%20skilled%20tradesman%20proud%20warm%20natural%20light%20interior&width=600&height=700&seq=about-story-v2&orientation=portrait"
                  alt="Premier Hardwood Floors craftsman Maryland"
                  className="w-full object-cover object-top"
                  style={{ height: '480px' }}
                />
              </div>
              {/* Stats overlay */}
              <div className="mt-5 bg-walnut rounded-2xl p-6 grid grid-cols-3 gap-4">
                {[
                  { num: '500+', label: 'Projects' },
                  { num: '15+', label: 'Years' },
                  { num: '5.0★', label: 'Rating' },
                ].map((s) => (
                  <div key={s.label} className="text-center">
                    <div className="font-serif text-2xl text-gold font-bold">{s.num}</div>
                    <div className="font-sans text-xs text-white/55 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="bg-sand py-28 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <span className="font-sans text-xs uppercase tracking-widest text-oak font-semibold">What Drives Us</span>
              <h2 className="font-serif text-4xl text-walnut font-bold mt-3">Our Mission &amp; Core Values</h2>
              <p className="font-sans text-charcoal/55 mt-4 max-w-xl mx-auto text-sm leading-relaxed">
                These aren&apos;t just words on a wall. They guide every estimate we write, every project we execute, and every call we take.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((v) => (
                <div key={v.title} className="bg-white rounded-2xl p-7 border border-sand-dark hover:border-oak/30 transition-colors group">
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-12 h-12 flex items-center justify-center bg-gold/10 rounded-xl group-hover:bg-gold/20 transition-colors">
                      <i className={`${v.icon} text-gold text-xl`} />
                    </div>
                    <div className="text-right">
                      <div className="font-serif text-xl text-walnut font-bold">{v.num}</div>
                      <div className="font-sans text-xs text-charcoal/45">{v.numLabel}</div>
                    </div>
                  </div>
                  <h3 className="font-sans text-base font-bold text-walnut mb-3">{v.title}</h3>
                  <p className="font-sans text-sm text-charcoal/60 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust / Credentials */}
        <section className="bg-walnut py-28 px-6 relative overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]"
            style={{ backgroundImage: 'repeating-linear-gradient(-45deg, #C8A96A 0, #C8A96A 1px, transparent 0, transparent 40%)', backgroundSize: '18px 18px' }}
          />
          <div className="relative max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <span className="font-sans text-xs uppercase tracking-widest text-gold font-semibold">Why Trust Us</span>
              <h2 className="font-serif text-4xl text-white font-bold mt-3">Credentials &amp; Credibility</h2>
              <p className="text-white/55 font-sans text-sm mt-4 max-w-md mx-auto">
                Fully vetted, licensed, and trusted by Maryland homeowners for over 15 years.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {trustItems.map((t) => (
                <div key={t.title} className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center hover:bg-white/10 transition-colors">
                  <div className="w-16 h-16 flex items-center justify-center bg-gold/15 rounded-2xl mx-auto mb-6">
                    <i className={`${t.icon} text-gold text-2xl`} />
                  </div>
                  <h3 className="font-sans text-base font-bold text-white mb-2">{t.title}</h3>
                  <p className="font-sans text-xs text-white/45 leading-relaxed">{t.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Experience */}
        <section className="bg-white py-28 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="font-sans text-xs uppercase tracking-widest text-oak font-semibold">Our Experience</span>
                <h2 className="font-serif text-4xl text-walnut font-bold mt-3 mb-7 leading-tight">
                  15+ Years of Hands-On Hardwood Expertise
                </h2>
                <div className="space-y-5 font-sans text-base text-charcoal/65 leading-relaxed">
                  <p>
                    With over 15 years of hands-on experience in hardwood floor installation, refinishing, and repair, our team has worked across hundreds of Maryland residential properties — from classic Annapolis colonials to modern Bethesda custom homes.
                  </p>
                  <p>
                    We&apos;ve encountered every challenge and solved every problem — complex herringbone pattern installations, severe water damage restoration, and historic floor preservation. That experience means you get confident, proven execution on every project.
                  </p>
                  <p>
                    Whether you&apos;re a first-time homeowner or on your fifth renovation, we bring the knowledge, tools, and craftsmanship to deliver results that last — and that you&apos;ll love coming home to.
                  </p>
                </div>
                <div className="mt-10 flex gap-4">
                  <button
                    onClick={() => setFormOpen(true)}
                    className="px-8 py-3.5 bg-walnut text-white rounded-xl font-sans text-sm font-bold hover:bg-walnut-dark transition-colors whitespace-nowrap"
                  >
                    Work With Us
                  </button>
                  <Link
                    to="/services"
                    className="px-8 py-3.5 border-2 border-walnut/20 text-walnut rounded-xl font-sans text-sm font-semibold hover:bg-sand transition-colors whitespace-nowrap"
                  >
                    View All Services
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-5">
                {experience.map((item) => (
                  <div key={item.title} className="bg-sand rounded-2xl p-7 border border-sand-dark hover:border-oak/30 transition-colors">
                    <div className="w-12 h-12 flex items-center justify-center bg-walnut/10 rounded-xl mb-5">
                      <i className={`${item.icon} text-oak text-xl`} />
                    </div>
                    <h3 className="font-sans text-sm font-bold text-walnut mb-2">{item.title}</h3>
                    <p className="font-sans text-xs text-charcoal/55 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <FinalCTA
          onEstimateClick={() => setFormOpen(true)}
          heading="Work With a Trusted Local Expert"
          subtext="Free in-home estimate. No obligation. Maryland's most trusted hardwood flooring specialists — since 2009."
        />
      </main>
      <Footer onEstimateClick={() => setFormOpen(true)} />
      <EstimateForm isOpen={formOpen} onClose={() => setFormOpen(false)} />
    </div>
  );
}
